/**
 * Hannu.
 *
 * He has been in the hot tub since some time last night. The skin has come off,
 * and the kamiina heating the water is down to embers. When you cross the yard
 * he climbs out, comes at you, and shouts '"Kimmo" hei, pist pari klapii!' while
 * trying to land a swipe.
 *
 * He gives up on distance and on nothing else. Leave his yard - `HANNU.leash`
 * from the tub - and he stops, but he does not get back in: he goes and stands
 * in the veräjä at the top of the yard, which is the way out towards the
 * juhlanavetta, and paces across it. He is not what stops you there; the shut
 * gate is (see `world/Kamiina.ts`). He is why it is shut, and he is what a
 * runner who arrives without a klapi has to stand next to while working that
 * out. Come back inside `HANNU.reengageRadius` and he comes off it again.
 *
 * That pair of rules is the whole of the palju now, because the klapit go in
 * one at a time and the pesä wants two. The rack is on the tub's north rim and
 * the firebox is off its east end, so paying the toll is two laps of the same
 * corner, and both of them are inside the radius he charges from. There is no
 * way to fetch the second log that is not a walk back into him. He used to
 * time out after seven seconds, which was fine when the toll was a button on
 * the way past and absurd the moment it became an errand - a man who wants
 * firewood does not stand and watch you go and get it.
 *
 * The pair ends all of it at once. From the step the second log goes in he
 * turns for the tub and never touches the player again - no swipe, no
 * knockback, no bite out of the vauhti meter - and he will not come out again
 * however many times you cross the yard. What you paid for is not a gate, it
 * is the man. The first log buys nothing except the fire lighting and the same
 * shout again, which between them are how the yard says "one down".
 *
 * ------------------------------------------------------------------------
 *  THE TUB IS A WALL, INCLUDING FOR HIM
 * ------------------------------------------------------------------------
 *  He is the only thing in the level that moves and is not collided with, and
 *  for a long time that meant the palju was solid to everybody except the man
 *  in it. He stood up in the middle of it, dropped 95 cm onto the water, and
 *  walked out through the staves at knee height on his way to you; going home
 *  he walked back in through them. Two of the three seconds either trip took
 *  were spent inside a barrel.
 *
 *  So the ring is his wall too, and there are two rules about it.
 *
 *    * His feet stay outside `rimRadius + bodyRadius` whenever he is on the
 *      yard - chasing, walking to the veräjä, coming home. A step that would
 *      land inside becomes a step *around*: see `walkTowards`. That is the
 *      one that matters for the route, because the errand is two laps of this
 *      tub and a man who could cut through the middle would not be running
 *      them.
 *
 *    * Crossing the ring is a climb and a hop rather than a walk. Standing up
 *      carries him out to the boards (`settle`), a hop takes him off them
 *      into the yard, and going home is the same two in reverse. `HANNU.hop*`
 *      is what that costs, and it is time he is in the air rather than
 *      swinging at you.
 *
 *  Both are geometry against one circle, which is why this is thirty lines
 *  rather than a pathfinder: the only obstacle in the yard he ever has to
 *  respect is round, and going round a circle is an angle.
 *
 *  And the way home ends in the water whatever happens on it. Walking to the
 *  rim is the route; `HANNU.returnPatience` is the guarantee, and after it he
 *  goes in through the staves rather than standing beside them. A man stuck
 *  outside his own palju is scenery with a bug in it, and the yard is the one
 *  place in the level where nobody is coming back to look at him.
 *
 * ------------------------------------------------------------------------
 *  WHY THIS IS NOT AN AI
 * ------------------------------------------------------------------------
 *  Hannu reacts to the player, but he has no randomness and no wall-clock
 *  dependency: his whole state is a pure function of (simulationTime, player
 *  position), stepped inside the fixed timestep. Feed the same inputs twice and
 *  you get the same Hannu twice, down to the frame he swings on. That is what
 *  makes him something you can learn a route around instead of a coin flip.
 *
 *  He is deliberately NOT a solid collider. A moving box in an open yard can pin
 *  the player against the sauna wall, and a run that can get stuck is worse than
 *  a run that can get shoved.
 *
 *  No three.js in here - the mesh lives in World.ts.
 * ------------------------------------------------------------------------
 */

import { HANNU, PLAYER } from '../config/gameplay';
import type { HannuActivation } from './Level';
import { clamp, type Vec3 } from '../utils/math';

export type HannuPhase =
  | 'submerged'
  | 'rising'
  | 'hopping'
  | 'chasing'
  | 'guarding'
  | 'returning'
  | 'sinking';

export interface HannuState {
  phase: HannuPhase;
  /** Feet position. `y` dips below 0 while he is still in the tub. */
  position: Vec3;
  /** Simulation time he started climbing out, or null while submerged. */
  emergedAt: number | null;
  /** Simulation time of his last swipe. */
  lastAttackAt: number;
  /** Shout is visible until this simulation time. */
  shoutUntil: number;
  /** How far out of the water he is, 0..1. Drives the mesh height. */
  rise: number;
  /** Which way he is facing, for the mesh. */
  yaw: number;
  swipes: number;
  /**
   * Which side of the tub he is getting out on, as an angle from its centre.
   *
   * Everything that crosses the rim is radial along this: standing up out of
   * the water, the hop off the staves, the hop back onto them and the settle
   * back down. It tracks the player while he is standing up - he gets out on
   * the side you are on - and is frozen for the length of a hop, so a hop is
   * a straight line over the boards rather than a curve round them.
   */
  rimAngle: number;
  /** How far through the current hop over the rim he is, 0..1. */
  hopProgress: number;
  /** Which way that hop goes: into the tub, or out of it. */
  hopInward: boolean;
  /**
   * When he set off for the tub, or null while he is not on his way to it.
   *
   * It is the clock on `HANNU.returnPatience`, and the only thing in him that
   * exists for a state he is not supposed to be able to reach: a man who has
   * been walking home for six seconds is not walking home.
   */
  returningSince: number | null;
}

export interface HannuEvent {
  /** He just came out of the tub this step. */
  emerged: boolean;
  /** He started shouting this step. */
  shouted: boolean;
  /** He landed a swipe this step. */
  hit: boolean;
  /** Unit direction the player should be shoved, when `hit`. */
  hitDirectionX: number;
  hitDirectionZ: number;
}

const NO_EVENT: HannuEvent = {
  emerged: false,
  shouted: false,
  hit: false,
  hitDirectionX: 0,
  hitDirectionZ: 0,
};

export interface HannuHome {
  /** Repeatable variation in the side he climbs out on, in radians. */
  exitAngle?: number;
  /** Centre of the hot tub in world XZ. */
  x: number;
  z: number;
  /** Where the water ends and the staves start. */
  radius: number;
  /** And where the staves end, which is the outside of the tub. */
  rimRadius: number;
  /** Rim height - he stands on the rim before dropping into the yard. */
  rimHeight: number;
  /** Centre of the veräjä's opening: where he stands guard, in world XZ. */
  gateX: number;
  gateZ: number;
  /** Entire player must enter the yard before he notices them. */
  activation?: HannuActivation;
}

export class Hannu {
  readonly state: HannuState;

  /**
   * The circle his feet may not enter: the staves, plus the width of a man.
   *
   * `bodyRadius` is in it because what has to stay out of the tub is Hannu and
   * not the point between his feet. Stand him on the boards' own edge and half
   * of him is over the water.
   */
  private readonly keepOut: number;

  /** And where he stands when he is up on the rim: the middle of the boards. */
  private readonly rimStand: number;

  constructor(
    private readonly home: HannuHome,
    private readonly config = HANNU,
  ) {
    this.keepOut = home.rimRadius + config.bodyRadius;
    this.rimStand = (home.radius + home.rimRadius) / 2;
    this.state = {
      phase: 'submerged',
      position: { x: home.x, y: -config.submergedDepth, z: home.z },
      emergedAt: null,
      lastAttackAt: -Infinity,
      shoutUntil: -Infinity,
      rise: 0,
      yaw: 0,
      swipes: 0,
      rimAngle: 0,
      hopProgress: 0,
      hopInward: false,
      returningSince: null,
    };
  }

  reset(): void {
    const s = this.state;
    s.phase = 'submerged';
    s.position.x = this.home.x;
    s.position.y = -this.config.submergedDepth;
    s.position.z = this.home.z;
    s.emergedAt = null;
    s.lastAttackAt = -Infinity;
    s.shoutUntil = -Infinity;
    s.rise = 0;
    s.yaw = 0;
    s.swipes = 0;
    s.rimAngle = 0;
    s.hopProgress = 0;
    s.hopInward = false;
    s.returningSince = null;
  }

  isShouting(simulationTime: number): boolean {
    return simulationTime < this.state.shoutUntil;
  }

  /**
   * Where he wants to be standing this instant: a metre inside the veräjä,
   * sliding `paceWidth` across its mouth once every `pacePeriod` seconds.
   *
   * A metre *inside* rather than in the opening itself, because the opening is
   * two metres wide and a man in the middle of it would be something to get
   * wedged against. He blocks it by being there, not by being solid.
   */
  private guardPoint(simulationTime: number): { x: number; z: number } {
    const c = this.config;
    const openingX = this.home.gateX;
    const openingZ = this.home.gateZ;

    // Into the yard, away from the gate: straight back towards the tub.
    const inX = this.home.x - openingX;
    const inZ = this.home.z - openingZ;
    const inward = Math.hypot(inX, inZ) || 1;

    // And across it, at right angles to that. One sweep is out and back.
    const cycle = simulationTime / c.pacePeriod - Math.floor(simulationTime / c.pacePeriod);
    const across = (cycle < 0.5 ? cycle * 4 - 1 : 3 - cycle * 4) * (c.paceWidth / 2);

    return {
      x: openingX + (inX / inward) * c.guardStandoff + (-inZ / inward) * across,
      z: openingZ + (inZ / inward) * c.guardStandoff + (inX / inward) * across,
    };
  }

  /**
   * One step towards a point on the yard, and never through the palju.
   *
   * He is not a collider and nothing collides with him, so this is the only
   * thing in the game that keeps him out of his own tub - and it has to,
   * because the errand the level is built on is two laps of that tub. A man
   * who can cut across the middle of it is not running the lap you are.
   *
   * A straight step that would land inside the ring becomes a step *around*
   * it: one arc of the keep-out circle, the way that closes the angle to
   * where he was going. That is a slide along an obstacle, which is what the
   * player's own collision does with a wall, and it is written as an angle
   * rather than as a push back out because a push stalls. Aim a man at a point
   * directly across the tub and every step in is exactly undone by the step
   * out; he stands there shuffling instead of walking round.
   */
  private walkTowards(targetX: number, targetZ: number, speed: number, dt: number): void {
    const s = this.state;
    const toX = targetX - s.position.x;
    const toZ = targetZ - s.position.z;
    const distance = Math.hypot(toX, toZ);
    if (distance <= 1e-4) return;

    const step = Math.min(speed * dt, distance);
    const nextX = s.position.x + (toX / distance) * step;
    const nextZ = s.position.z + (toZ / distance) * step;
    if (Math.hypot(nextX - this.home.x, nextZ - this.home.z) >= this.keepOut) {
      s.position.x = nextX;
      s.position.z = nextZ;
      return;
    }

    const angle = Math.atan2(s.position.z - this.home.z, s.position.x - this.home.x);
    // Which way round is quicker is which way the target lies along the
    // tangent. Dead astern of the tub the two are equal, and it has to still
    // pick one: right, always, because a tie broken by anything else is a tie
    // broken differently on somebody else's machine.
    const side = toX * -Math.sin(angle) + toZ * Math.cos(angle) >= 0 ? 1 : -1;
    const swept = angle + (side * step) / this.keepOut;
    s.position.x = this.home.x + Math.cos(swept) * this.keepOut;
    s.position.z = this.home.z + Math.sin(swept) * this.keepOut;
  }

  /**
   * Where he is when he is `rise` of the way out of the water: sat in the
   * middle of the tub at 0, stood on the rim at 1.
   *
   * Both halves of that move together on purpose. Standing up in a tub is not
   * a lift, it is a man crossing his own bath to the side he means to get out
   * on - so the same number that takes his head up takes him out to the
   * staves, and it runs backwards on the way in.
   */
  private settle(rise: number): void {
    const s = this.state;
    const reach = this.rimStand * rise;
    s.position.x = this.home.x + Math.cos(s.rimAngle) * reach;
    s.position.z = this.home.z + Math.sin(s.rimAngle) * reach;
    const c = this.config;
    s.position.y = -c.submergedDepth + rise * (c.submergedDepth + this.home.rimHeight);
  }

  /** Distance from the player, for the debug overlay. */
  distanceTo(playerPosition: Vec3): number {
    return Math.hypot(
      playerPosition.x - this.state.position.x,
      playerPosition.z - this.state.position.z,
    );
  }

  /**
   * One fixed simulation step. Call before stepping the player.
   *
   * `fireIsFed` is nearly the only thing he knows about the world besides
   * where you are: it is what turns him from a man standing in a gateway into
   * a man getting back into his bath. `klapiJustBurned` is the other half of
   * it, and it is only ever true on the step a log went in - which, while the
   * pesä still wants the second one, is a step he has something to say about.
   */
  simulate(
    simulationTime: number,
    dt: number,
    playerPosition: Vec3,
    fireIsFed: boolean,
    klapiJustBurned = false,
  ): HannuEvent {
    const s = this.state;
    const c = this.config;
    const event: HannuEvent = { ...NO_EVENT };

    const toPlayerX = playerPosition.x - s.position.x;
    const toPlayerZ = playerPosition.z - s.position.z;
    const distance = Math.hypot(toPlayerX, toPlayerZ);
    if (distance > 1e-4) {
      s.yaw = Math.atan2(-toPlayerX, -toPlayerZ);
    }

    // How far into his yard you are. Every one of his distances is measured
    // from the tub rather than from him, so `triggerRadius`, `leash` and
    // `reengageRadius` are three marks on one ruler and can be reasoned about
    // as a single picture of the yard.
    const playerFromTub = Math.hypot(
      playerPosition.x - this.home.x,
      playerPosition.z - this.home.z,
    );

    // One klapi in, and the pesä still wants the other. He is not finished,
    // and he says so with the line he has always had - he asked for a *pari*,
    // and this is the moment that word starts doing work.
    if (klapiJustBurned && !fireIsFed && s.phase !== 'submerged') {
      s.shoutUntil = simulationTime + c.shoutDuration;
      event.shouted = true;
    }

    switch (s.phase) {
      case 'submerged': {
        // A fire with a log in it is a man with nothing to shout about. Once it
        // is fed he stays in the water however many times you come past - and
        // that matters, because the veräjä is inside his trigger radius and a
        // runner who has just paid the toll would otherwise be met by the man
        // he paid it to.
        if (fireIsFed) break;
        const entry = this.home.activation;
        const entryX = entry
          ? (playerPosition.z + PLAYER.radius >= entry.carsMinZ ? entry.carsX : entry.cableX)
          : -Infinity;
        const insideFence = !entry || playerPosition.x + PLAYER.radius < entry.fenceX;
        if (playerFromTub <= c.triggerRadius && playerPosition.x > entryX + PLAYER.radius && insideFence) {
          s.phase = 'rising';
          s.emergedAt = simulationTime;
          s.shoutUntil = simulationTime + c.shoutDuration;
          event.emerged = true;
          event.shouted = true;
        }
        break;
      }

      case 'rising': {
        // Fed while he was still climbing out: he settles straight back down,
        // the way he came, without ever getting a foot onto the boards.
        if (fireIsFed) {
          s.phase = 'sinking';
          break;
        }
        // He gets out on the side you are on, and he is still choosing which
        // side that is while he stands up. Frozen at the top of the rise: from
        // there on he is committed to one place on the rim, which is what
        // makes going the long way round the tub worth anything.
        const outX = playerPosition.x - this.home.x;
        const outZ = playerPosition.z - this.home.z;
        if (Math.hypot(outX, outZ) > 1e-4) {
          s.rimAngle = Math.atan2(outZ, outX) + (this.home.exitAngle ?? 0);
        }

        const elapsed = simulationTime - (s.emergedAt ?? simulationTime);
        s.rise = clamp(elapsed / c.riseDuration, 0, 1);
        this.settle(s.rise);
        if (s.rise >= 1) {
          // Up on the staves, and the yard is 95 cm below him.
          s.phase = 'hopping';
          s.hopInward = false;
          s.hopProgress = 0;
        }
        break;
      }

      case 'hopping': {
        // Over the rim, either way round, and the one part of him that is not
        // a man walking: he is in the air, so he cannot be steered, cannot
        // swipe and cannot be called off. Both hops are short enough that the
        // last of those is not a hole in "the klapi ends it mid-stride" - the
        // step he lands on is the step he turns for the tub.
        s.rise = 1;
        s.hopProgress = Math.min(1, s.hopProgress + dt / c.hopDuration);
        const t = s.hopProgress;
        const fromRadius = s.hopInward ? this.keepOut : this.rimStand;
        const toRadius = s.hopInward ? this.rimStand : this.keepOut;
        const fromY = s.hopInward ? 0 : this.home.rimHeight;
        const toY = s.hopInward ? this.home.rimHeight : 0;

        const reach = fromRadius + (toRadius - fromRadius) * t;
        s.position.x = this.home.x + Math.cos(s.rimAngle) * reach;
        s.position.z = this.home.z + Math.sin(s.rimAngle) * reach;
        // A hop rather than a slide down a ramp, and the arch is pinned to the
        // boards rather than to the two ends of it: halfway through it his
        // feet are `hopClearance` above the rim, whichever way round he is
        // going. Pin it to the ends instead and the curve is only as high as
        // the drop happens to be - which for the way *in* is a man dragging
        // his feet through the staves he is supposed to be clearing.
        const midY = (fromY + toY) / 2;
        const halfway = this.home.rimHeight + c.hopClearance;
        s.position.y = fromY + (toY - fromY) * t + 4 * (halfway - midY) * t * (1 - t);

        if (s.hopInward) {
          const toHomeX = this.home.x - s.position.x;
          const toHomeZ = this.home.z - s.position.z;
          s.yaw = Math.atan2(-toHomeX, -toHomeZ);
        }

        if (t >= 1) {
          s.position.y = toY;
          // Landed. In the tub he starts sinking; out of it he comes after you
          // - unless the pesä got its pari while he was in the air, in which
          // case he turns round on the spot he landed on.
          s.phase = s.hopInward ? 'sinking' : fireIsFed ? 'returning' : 'chasing';
        }
        break;
      }

      case 'chasing': {
        s.rise = 1;
        s.position.y = 0;

        // The klapi ends it mid-stride. He wanted a log in his kamiina and he
        // has one, so the swipe he was winding up never lands and the metre of
        // vauhti it would have cost is never taken. This is checked *before* he
        // moves and before he can attack, which is the whole of what "as soon
        // as the klapi is in, Hannu stops" means: the same step, not the next
        // one, and not when the chase happens to time out.
        if (fireIsFed) {
          s.phase = 'returning';
          break;
        }

        // He gives up on distance and on nothing else. There used to be seven
        // seconds on it too, and that was the right rule for a toll you paid
        // on the way past - but the klapit go in one at a time now, so the
        // errand is two laps of the tub and a clock that runs out halfway
        // through it would leave the man who wants the wood standing there
        // watching somebody fetch it. Leave his yard and he is done with you;
        // stay in it and he is not.
        const hannuFromTub = Math.hypot(s.position.x - this.home.x, s.position.z - this.home.z);
        if (playerFromTub > c.leash || hannuFromTub > c.leash) {
          // Out of his yard, and the fire still needs feeding: he goes and
          // stands in the veräjä rather than getting back in.
          s.phase = 'guarding';
          break;
        }

        this.walkTowards(playerPosition.x, playerPosition.z, c.speed, dt);

        if (distance <= c.reach && simulationTime - s.lastAttackAt >= c.attackCooldown) {
          s.lastAttackAt = simulationTime;
          s.shoutUntil = simulationTime + c.shoutDuration;
          s.swipes++;
          event.hit = true;
          event.shouted = true;
          // Straight away from him: caught from behind, this is a speed boost.
          const away = distance > 1e-6 ? distance : 1;
          event.hitDirectionX = toPlayerX / away;
          event.hitDirectionZ = toPlayerZ / away;
        }
        break;
      }

      case 'guarding': {
        s.rise = 1;
        s.position.y = 0;
        if (fireIsFed) {
          s.phase = 'returning';
          break;
        }

        // Come back into the yard and he comes off the gate. This is what a
        // second klapi actually costs: the rack is on the tub's north rim, the
        // rim is deep inside `reengageRadius`, and there is no way to fetch
        // one that is not a walk back into him. He gave up on distance, so
        // distance is what un-gives-up him.
        //
        // The gap between this radius and the `leash` he broke off at is the
        // hysteresis, and it is load-bearing: re-engaging at the same distance
        // he quit at would be a man flickering across a line rather than a man
        // changing his mind.
        if (playerFromTub <= c.reengageRadius) {
          s.phase = 'chasing';
          s.shoutUntil = simulationTime + c.shoutDuration;
          event.shouted = true;
          break;
        }

        // Walk to the mouth of the veräjä, then pace across it. The pace is a
        // triangle wave on simulationTime and nothing else, so two identical
        // runs still meet him at the identical end of the identical sweep.
        const post = this.guardPoint(simulationTime);
        this.walkTowards(post.x, post.z, c.guardSpeed, dt);

        if (distance <= c.reach && simulationTime - s.lastAttackAt >= c.attackCooldown) {
          s.lastAttackAt = simulationTime;
          s.shoutUntil = simulationTime + c.shoutDuration;
          s.swipes++;
          event.hit = true;
          event.shouted = true;
          const away = distance > 1e-6 ? distance : 1;
          event.hitDirectionX = toPlayerX / away;
          event.hitDirectionZ = toPlayerZ / away;
        }
        break;
      }

      case 'returning': {
        // Home is the tub, but the way in is over the side of it: he walks up
        // to the staves and stops there. He used to walk to the middle, which
        // meant the last three metres of a man going home were three metres of
        // man inside the boards.
        if (s.returningSince === null) s.returningSince = simulationTime;
        const toHomeX = this.home.x - s.position.x;
        const toHomeZ = this.home.z - s.position.z;
        const homeDistance = Math.hypot(toHomeX, toHomeZ);
        s.yaw = Math.atan2(-toHomeX, -toHomeZ);

        // Six seconds out here and the tub takes him anyway: onto the boards
        // where he stands, and down from there. That is a teleport through the
        // staves and it is meant to be - the walk home is 2.6 s from the far
        // end of the leash, so nothing that trips this is a man walking, and
        // the alternative to going through the side of the palju is standing
        // beside it until somebody restarts the run. See `HANNU.returnPatience`.
        if (simulationTime - s.returningSince > c.returnPatience) {
          s.rimAngle = Math.atan2(-toHomeZ, -toHomeX);
          s.rise = 1;
          this.settle(1);
          s.returningSince = null;
          s.phase = 'sinking';
          break;
        }

        // The last stride is a whole stride or it is arriving, and never the
        // sliver of one that is left. He used to walk to *exactly* `keepOut`
        // and then test for being there, and out at the palju's own
        // coordinates that test never came true: the gap shrinks to a
        // femtometre, a femtometre is finer than one ulp of x = 119, and
        // adding it to his position moves nothing at all. He stood at the
        // side of his own tub for the rest of the run. Arriving is "near
        // enough to reach it this step" instead, which is a comparison
        // rounding cannot defeat, and it costs him at most one 2.5 cm snap
        // onto the ring - which the hop is about to overwrite anyway.
        const stride = c.speed * 0.7 * dt;
        if (homeDistance - this.keepOut > stride) {
          s.position.x += (toHomeX / homeDistance) * stride;
          s.position.z += (toHomeZ / homeDistance) * stride;
          break;
        }
        // At the side of it, on whichever side of it the chase left him.
        s.rimAngle = Math.atan2(-toHomeZ, -toHomeX);
        s.hopInward = true;
        s.hopProgress = 0;
        s.returningSince = null;
        s.phase = 'hopping';
        break;
      }

      case 'sinking': {
        // Down off the rim and back to his seat in the middle, which is the
        // rise run backwards - see `settle`.
        s.rise = Math.max(0, s.rise - dt / c.sinkDuration);
        this.settle(s.rise);
        if (s.rise <= 0) {
          // Back in the tub, and staying in it: nothing gets him out again
          // once the pesä has its pari, and nothing but the pari gets him here.
          s.phase = 'submerged';
          s.emergedAt = null;
          s.position.x = this.home.x;
          s.position.z = this.home.z;
          s.position.y = -c.submergedDepth;
        }
        break;
      }
    }

    return event;
  }
}
